import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { createLogger } from '@/lib/logger'
import { isMainWindow, listenForQuickCapture } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { getOrgPath } from '@/utils/org-paths'

const logger = createLogger('desktop/quick-capture-controller')

/**
 * Desktop-only bridge for the global quick-capture hotkey
 * (CommandOrControl+Shift+K). Rust shows and focuses the main window, then
 * emits 'quick-capture'; we open the same Add Task composer the header button
 * opens.
 *
 * There is no global create-task store, so we reuse the two paths the app
 * already uses to open the composer programmatically:
 *   1. click the header's `[data-create-task]` button when a task surface is
 *      already mounted (same mechanism as the empty-state CTA registry), or
 *   2. navigate to the workspace tasks page with `?action=create`, which
 *      TaskLayoutActions honours by opening the dialog on mount.
 */
export function QuickCaptureController() {
  const navigate = useNavigate()
  const currentOrganizationSlug = useOrganizationStore(
    state => state.currentOrganization?.slug,
  )

  useEffect(() => {
    let cancelled = false
    let unlisten: (() => void) | undefined

    async function subscribe() {
      // One tray/companion/focus window per app must not fight over the
      // composer — only the main window responds.
      const isMain = await isMainWindow()
      if (!isMain || cancelled) return

      unlisten = await listenForQuickCapture(() => {
        const createButton =
          document.querySelector<HTMLElement>('[data-create-task]')

        if (createButton) {
          createButton.click()
          return
        }

        if (!currentOrganizationSlug) {
          logger.debug('Quick capture ignored: no current organization')
          return
        }

        navigate(`${getOrgPath(currentOrganizationSlug, '/tasks')}?action=create`)
      })

      if (cancelled) {
        unlisten()
        unlisten = undefined
      }
    }

    void subscribe()

    return () => {
      cancelled = true
      unlisten?.()
    }
  }, [navigate, currentOrganizationSlug])

  return null
}
